import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../../../utils/type';

type BillingCycleToggleProps = {
  isAnnual: boolean;
  onChange: (isAnnual: boolean) => void;
  className?: string;
  /** Slightly tighter padding (e.g. compare-plans header). */
  compact?: boolean;
};

/**
 * Yearly / Monthly billing switch.
 * The sliding highlight tracks the active button's real width so "Pay Yearly"
 * (including UP TO 17% OFF) is fully covered — not a hard-coded 50/50 split.
 */
export default function BillingCycleToggle({
  isAnnual,
  onChange,
  className,
  compact = false,
}: BillingCycleToggleProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const yearlyRef = useRef<HTMLButtonElement>(null);
  const monthlyRef = useRef<HTMLButtonElement>(null);
  const [slider, setSlider] = useState({ left: 4, width: 0 });

  const updateSlider = () => {
    const active = isAnnual ? yearlyRef.current : monthlyRef.current;
    if (!active) return;

    // Yearly: end the highlight just after "UP TO 17% OFF" with a small gap,
    // instead of stretching through the full button padding.
    if (isAnnual) {
      const badge = active.lastElementChild as HTMLElement | null;
      const contentEnd = badge
        ? badge.offsetLeft + badge.offsetWidth
        : active.offsetWidth;
      const padAfterBadge = 8;
      setSlider({
        left: active.offsetLeft,
        width: Math.min(active.offsetWidth, contentEnd + padAfterBadge),
      });
      return;
    }

    setSlider({
      left: active.offsetLeft,
      width: active.offsetWidth,
    });
  };

  useLayoutEffect(() => {
    updateSlider();
  }, [isAnnual]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(() => updateSlider());
    ro.observe(container);
    if (yearlyRef.current) ro.observe(yearlyRef.current);
    if (monthlyRef.current) ro.observe(monthlyRef.current);
    window.addEventListener('resize', updateSlider);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', updateSlider);
    };
  }, [isAnnual]);

  const yearlyPad = compact
    ? 'pl-4 sm:pl-5 pr-1.5 py-1.5'
    : 'pl-3 sm:pl-5 pr-1.5 py-2';
  const monthlyPad = compact ? 'px-4 sm:px-5 py-1.5' : 'px-3 sm:px-5 py-2';

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative inline-flex items-center rounded-full bg-muted p-1 shadow-inner',
        className
      )}
    >
      <button
        ref={yearlyRef}
        type="button"
        onClick={() => onChange(true)}
        className={cn(
          'relative z-10 rounded-full text-xs sm:text-sm font-semibold transition-colors flex items-center gap-2',
          yearlyPad,
          isAnnual
            ? 'text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        Pay Yearly
        <span
          className={cn(
            'text-[10px] sm:text-xs font-bold px-2 py-0.5 rounded-full transition-colors',
            isAnnual ? 'text-white' : 'text-[color:var(--accent-orange)]'
          )}
          style={{
            background: isAnnual
              ? 'var(--accent-orange)'
              : 'color-mix(in srgb, var(--accent-orange) 20%, transparent)',
          }}
        >
          UP TO 17% OFF
        </span>
      </button>
      <button
        ref={monthlyRef}
        type="button"
        onClick={() => onChange(false)}
        className={cn(
          'relative z-10 rounded-full text-xs sm:text-sm font-semibold transition-colors',
          monthlyPad,
          !isAnnual
            ? 'text-primary-foreground'
            : 'text-muted-foreground hover:text-foreground'
        )}
      >
        Pay Monthly
      </button>
      <div
        className="pointer-events-none absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-out"
        style={{
          background: 'var(--gradient-primary)',
          left: slider.left,
          width: slider.width || undefined,
        }}
      />
    </div>
  );
}
